import { useEffect, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { useCityStore } from '../../city/cityStore';
import { useWorldStore } from '../../state/worldStore';
import { serverNow } from '../../services/worldSocket';
import { audioEngine } from '../../services/audioEngine';
import { cutsceneBridge } from '../../city/cutscene';
import { walled, falloff, setLevel } from '../../city/hearing';
import { stageAudio, cityAudio, micWords } from '../../city/stageBridge';
import { takeAudio, releaseAudio, onAudio } from '../../services/audioPool';

// CityStage — the plaza stage, with the music (Khari, 2026-10-23: "A song being
// played while the artist performs on the stage").
//
// The server (API city/CityStageService) sends each set as songs on the city
// clock: when each starts and for how long. Here they are played from the
// stage, at the right bar for everyone, loud on the square and carrying a
// little off it, like a PA. While you are at the show the station steps aside
// (the room hold: it fades out and comes back after), and the player bar says
// who is on and what they are playing. The performer's head and face move with
// it (the `speaking` flag the bodies already read).
//
// And the DJ: a few minutes before each set the World's station host calls it,
// in their own words and voice (written server-side). It plays over the station
// (ducked under it) for anyone with the radio on out in the city.

const FULL = 7;           // units (~16 m): the square in front of the stage
const FAR = 24;           // ~55 m: off the square, then nothing
const WALLED = 0.3;       // a building between you and the stage
const HOLD_ON = 0.3;      // this close to a set: the station steps aside…
const HOLD_OFF = 0.1;     // …and comes back once you are this far from it for a few seconds
const EDGE_MS = 2500;     // fade in and out at the ends of the set
// The station goes quiet this long before the first song (the act walking on),
// and stays quiet through the gaps between songs until the set is over: Khari,
// 2026-10-28, "Between songs on the plaza stage, I can still hear the city radio
// station… A lot of users will forget to turn off the main radio before they
// listen in at the plaza.. we should mute it for them." (The hold followed the
// song playing, so it let go 4 s into each 9 s gap and the radio came back.)
const HUSH_MS = 15000;

// Hearing it (city/hearing, shared with the street's other music): the square
// in front of the stage at full level, falling off to nothing at FAR.
const levelAt = (d) => falloff(d, FULL, FAR);

export default function CityStage() {
  const camera = useThree((s) => s.camera);
  const els = useRef(new Map());        // `${a}:${t0}:${i}` → { el, started }
  const acc = useRef(0);
  const held = useRef(false);
  const quietSince = useRef(0);
  const said = useRef(new Set());       // DJ heads-ups already played here
  const dj = useRef(null);              // { el, key }

  // Leaving the street mid-set (into a venue, the plan view): the music stops and
  // the station comes back — never left held by a stage you can no longer hear.
  useEffect(() => () => {
    for (const e of els.current.values()) releaseAudio(e.el);
    els.current.clear();
    if (held.current) { held.current = false; audioEngine.roomQuiet(false, 900, 'stage').catch?.(() => {}); }
    if (dj.current) { releaseAudio(dj.current.el); dj.current = null; audioEngine.duck(false); }
    useCityStore.setState({ stageNow: null, djNow: null });
    stageAudio.playing = false;
  }, []);

  useFrame((_, dt) => {
    acc.current += dt;
    if (acc.current < 0.2) return;
    acc.current = 0;
    const cs = useCityStore.getState();
    if (!cs.live) return;
    const t = serverNow();
    const me = (cutsceneBridge.active && cutsceneBridge.at) || camera.position;
    // Paused from the player (Hud ⏯ while it shows the stage): the set carries on,
    // still "what is on" (stageNow) and still holding the radio — only silent.
    const heard = useWorldStore.getState().cityMusicOff ? 0 : 1;
    const places = cs.snap?.places || [];
    const keep = new Set();
    let best = 0; let now = null;
    let presence = 0;     // how close you are to a set that is on (gaps between songs included)

    for (const sh of cs.shows || []) {
      // A SET INSIDE A VENUE (2026-10-30) plays in its room (VenueStage), never
      // out here: only a place with an outdoor stage is played from the street.
      if (sh.inside) continue;
      const pl = places.find((p) => p.slug === (sh.place || 'street-plaza'));
      const at = pl?.stage?.at;
      if (!at) continue;
      // Watching it in scene view (CityCutscene): front-of-stage, steady, for the whole set.
      const watching = cutsceneBridge.scene?.kind === 'stage' && cutsceneBridge.scene.id === `${sh.a}:${sh.t0}`;
      const d = watching ? 0 : Math.hypot(me.x - at[0], me.z - at[1]);
      const on = t >= sh.t0 - 30000 && t < sh.t1;
      const muffle = on && d > FULL && d < FAR && walled(me.x, me.z, at[0], at[1]) ? WALLED : 1;
      if (t >= sh.t0 - HUSH_MS && t < sh.t1) presence = Math.max(presence, levelAt(d) * muffle);
      if (on) {
        // The song on now (and the next one, fetched ahead).
        let i = -1;
        for (let k = 0; k < (sh.tracks || []).length; k++) if (t >= sh.t0 + sh.tracks[k].at) i = k;
        for (const k of [i, i + 1]) {
          const tr = sh.tracks?.[k];
          if (!tr?.url) continue;
          const key = `${sh.a}:${sh.t0}:${k}`;
          keep.add(key);
          let e = els.current.get(key);
          if (!e && d < FAR + 6) {
            // Borrowed, never `new Audio()`: on iOS only an element blessed on a
            // tap may start from here (services/audioPool).
            const el = takeAudio(tr.url);
            el.muted = true;
            try { el.load(); } catch { /* loads on play */ }
            e = { el, started: false };
            els.current.set(key, e);
          }
          if (!e || k !== i) continue;
          const off = t - (sh.t0 + tr.at);
          // Between songs. Silenced, not just left: a file longer than its listed
          // length played its tail over the closing words (2026-10-29).
          if (off >= tr.durMs) { if (e.started) setLevel(e.el, 0); continue; }
          const edge = Math.min(1, (sh.t1 - t) / EDGE_MS, (t - sh.t0) / EDGE_MS + 0.3);
          const v = levelAt(d) * muffle * Math.max(0, edge);
          cs.setSpeaking(sh.a, sh.t0 + tr.at + tr.durMs);
          if (v > 0.01) {
            if (!e.started) {
              e.started = true;
              e.tried = performance.now();
              try { e.el.currentTime = off / 1000; } catch { /* on metadata */ }
              onAudio(e.el, 'loadedmetadata', () => { try { e.el.currentTime = (serverNow() - (sh.t0 + tr.at)) / 1000; } catch { /* ignore */ } }, { once: true });
              e.el.play().catch((err) => { e.playErr = err?.name || String(err); });
            } else if (e.el.paused && performance.now() - (e.tried || 0) > 2000) {
              /* ⚠ A REFUSED START IS TRIED AGAIN (2026-10-30: on an iPhone the
                 plaza's song stayed paused with the pool healthy — its one play()
                 had been refused and nothing ever asked again). Every 2 s while
                 it should be sounding, from where the set is now. */
              e.tried = performance.now();
              if (e.el.readyState >= 1) { try { e.el.currentTime = off / 1000; } catch { /* ignore */ } }
              e.el.play().then(() => { e.playErr = null; }).catch((err) => { e.playErr = err?.name || String(err); });
            } else if (e.el.readyState >= 2 && Math.abs(e.el.currentTime - off / 1000) > 0.8) {
              try { e.el.currentTime = off / 1000; } catch { /* ignore */ }
            }
          }
          setLevel(e.el, v * heard);
          // Where the song really is, for scene view to cut to (city/stageBridge).
          if (e.started) {
            stageAudio.key = key; stageAudio.pos = e.el.currentTime; stageAudio.at = performance.now();
            stageAudio.playing = !e.el.paused && e.el.readyState >= 2;
            // What the element itself says, for ?audiodebug (components/AudioDebug).
            let host = '?';
            try { host = e.el.src.startsWith('data:') ? 'data' : new URL(e.el.src, window.location.href).host || 'relative'; } catch { /* keep ? */ }
            stageAudio.dbg = { paused: e.el.paused, ready: e.el.readyState, net: e.el.networkState, err: e.el.error?.code || null, muted: e.el.muted, playErr: e.playErr || null, host, rel: !/^(https?:|data:)/.test(tr.url || '') };
          }
          if (v > best) { best = v; now = { a: sh.a, name: sh.name, title: tr.title, art: tr.art || null }; }
        }
        // THEIR OWN WORDS ON THE MIC (Khari, 2026-10-29: "artists give opening
        // and closing statements"; API Stage.planSet): before the first song,
        // between songs in a longer set (2026-10-30), and after the last, at the
        // stage's level, the face speaking. A late arrival hears the rest of
        // what is being said.
        for (const [which, w] of micWords(sh)) {
          if (!w?.audio) continue;
          const start = sh.t0 + (w.at || 0);
          const end = start + (Number(w.dur) || 3) * 1000;
          // Fetched 8 s ahead, not 30: every player held early is one the pool
          // cannot lend the songs (iOS only plays tap-blessed ones).
          if (t < start - 8000 || t > end + 500) continue;
          const key = `${sh.a}:${sh.t0}:${which}`;
          keep.add(key);
          let e = els.current.get(key);
          if (!e && d < FAR + 6) {
            const el = takeAudio(w.audio);
            el.muted = true;
            try { el.load(); } catch { /* loads on play */ }
            e = { el, started: false };
            els.current.set(key, e);
          }
          if (!e || t < start || t >= end) continue;
          const v = levelAt(d) * muffle;
          cs.setSpeaking(sh.a, end);
          if (v > 0.01 && !e.started) {
            e.started = true;
            e.tried = performance.now();
            try { e.el.currentTime = (t - start) / 1000; } catch { /* on metadata */ }
            e.el.play().catch((err) => { e.playErr = err?.name || String(err); });
          } else if (v > 0.01 && e.el.paused && !e.el.ended && performance.now() - (e.tried || 0) > 2000) {
            // A refused start is tried again, like the songs' (2026-10-30: Avva's
            // opening was written and on time, and nobody heard it).
            e.tried = performance.now();
            if (e.el.readyState >= 1) { try { e.el.currentTime = (t - start) / 1000; } catch { /* ignore */ } }
            e.el.play().then(() => { e.playErr = null; }).catch((err) => { e.playErr = err?.name || String(err); });
          }
          setLevel(e.el, v * heard);
          if (v > best) { best = v; now = { a: sh.a, name: sh.name, title: 'on the mic', art: null }; }
        }
      }

      // THE DJ CALLS IT — once, around its moment, for anyone with the radio on
      // out here (not over a set that is already playing at them).
      const an = sh.announce;
      const akey = `${sh.a}:${sh.t0}`;
      if (an?.audio && !said.current.has(akey) && t >= an.at && t < an.at + 20000) {
        said.current.add(akey);
        const ws = useWorldStore.getState();
        if (ws.entered && ws.musicOn && !held.current && !dj.current) {
          const el = takeAudio(an.audio);
          dj.current = { el, key: akey };
          audioEngine.duck(true);
          let over = false;
          const done = () => {
            if (over) return;
            over = true;
            audioEngine.duck(false);
            if (dj.current?.el === el) { dj.current = null; releaseAudio(el); }
            const cur = useCityStore.getState().djNow;
            if (cur?.key === akey) useCityStore.setState({ djNow: null });
          };
          onAudio(el, 'ended', done, { once: true });
          onAudio(el, 'error', done, { once: true });
          setTimeout(done, Math.max(4000, (an.dur || 10) * 1000 + 1500));
          el.play().catch(done);
          useCityStore.setState({ djNow: { key: akey, host: an.host, text: String(an.text || '').replace(/\[[^\]]{1,24}\]\s*/g, ''), until: Date.now() + (an.dur || 10) * 1000 } });
        }
      }
    }

    // Songs that are over, or far: let them go.
    for (const [key, e] of els.current) {
      if (keep.has(key)) continue;
      releaseAudio(e.el);
      els.current.delete(key);
    }

    // The station steps aside while you are at the show, the whole set long, and
    // comes back after (or once you walk away from it).
    cityAudio.stagePresence = presence;
    if (presence >= HOLD_ON && !held.current) { held.current = true; audioEngine.roomQuiet(true, 900, 'stage').catch?.(() => {}); }
    if (held.current) {
      if (presence > HOLD_OFF) quietSince.current = 0;
      else if (!quietSince.current) quietSince.current = Date.now();
      else if (Date.now() - quietSince.current > 4000) { held.current = false; quietSince.current = 0; audioEngine.roomQuiet(false, 900, 'stage').catch?.(() => {}); }
    }

    // What the player bar says while you can hear it.
    const show = best > 0.15 ? now : null;
    const cur = cs.stageNow;
    if ((cur?.title || null) !== (show?.title || null) || (cur?.a || null) !== (show?.a || null)) useCityStore.setState({ stageNow: show });
  });

  return null;
}
